import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Form, Modal } from "react-bootstrap";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { FaEnvelope, FaMapMarkerAlt, FaPhoneAlt, FaEye, FaWhatsapp } from "react-icons/fa";
import {
  getBookings,
  addVendorPayment,
  addCustomerPayment, generateinvoice, sendInvoice, getInvoice
} from "../../store/slices/booking";
import { getVendor } from "../../store/slices/vendor";
import Select from "react-select";
import moment from "moment";

export default function BookingDetails() {
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const { id } = useParams();

  const [tab, setTab] = useState("overview");
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);

  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [invoiceType, setInvoiceType] = useState("advance");
  const [dueDate, setDueDate] = useState("");
  const [invoiceNotes, setInvoiceNotes] = useState("");
  const [vendors, setVendors] = useState([]);

  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);


  const [booking, setBooking] = useState(null);
  const [customerPayments, setCustomerPayments] = useState([]);
  const [vendorPayments, setVendorPayments] = useState([]);


  const [vendorAmount, setVendorAmount] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [component, setComponent] = useState("HOTEL");


  const [showVendorModal, setShowVendorModal] = useState(false);

  /* ================= FETCH ================= */

  useEffect(() => {
    const fetchBooking = async () => {
      setLoading(true)
      try {
        const res = await dispatch(getBookings({ id })).unwrap();
        const data = res?.data || {};

        setBooking(data.booking || null);
        setCustomerPayments(data.customerPayments || []);
        setVendorPayments(data.vendorPayments || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false)
      }
    };

    if (id) fetchBooking();
  }, [dispatch, id]);


  const fetchVendors = async (search = "") => {
    try {
      const res = await dispatch(
        getVendor({
          search: search,
        })
      ).unwrap();

      setVendors(res?.vendors || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load vendors");
    }
  };

  useEffect(() => {
    fetchVendors();
  }, [dispatch]);

  const vendorOptions = vendors.map((v) => ({
    value: v._id,
    label: v.name,
    data: v,
  }));

  /* ================= HANDLERS ================= */

  const handleMarkInvoicePaid = async (invoiceId) => {
    try {
      await dispatch(
        addCustomerPayment({
          invoiceId,
        })
      ).unwrap();

      toast.success("Invoice marked as paid");

      // Refresh booking details
      const res = await dispatch(getBookings({ id })).unwrap();
      const data = res?.data || {};

      setBooking(data.booking || null);
      setCustomerPayments(data.customerPayments || []);
      setVendorPayments(data.vendorPayments || []);

      // Refresh invoices
      const invoiceRes = await dispatch(
        getInvoice({
          bookingId: id,
        })
      ).unwrap();

      setInvoices(invoiceRes?.data || []);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to mark invoice as paid");
    }
  };

  useEffect(() => {
    const fetchBooking = async () => {
      setLoading(true);

      try {
        // Fetch booking details
        const res = await dispatch(getBookings({ id })).unwrap();
        const data = res?.data || {};

        setBooking(data.booking || null);
        setCustomerPayments(data.customerPayments || []);
        setVendorPayments(data.vendorPayments || []);

        // Fetch invoices for this booking
        const invoiceRes = await dispatch(
          getInvoice({
            bookingId: id,
          })
        ).unwrap();

        setInvoices(invoiceRes?.data || []);
      } catch (err) {
        console.error(err);
        toast.error("Failed to load booking details");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchBooking();
    }
  }, [dispatch, id]);

  const handleVendorPayment = async () => {
    try {
      if (!vendorAmount || !vendorId) return toast.error("Fill all fields");

      await dispatch(
        addVendorPayment({
          bookingId: id,
          vendorId,
          component,
          amount: Number(vendorAmount),
        }),
      ).unwrap();

      toast.success("Vendor payment added");

      setVendorPayments((prev) => [
        ...prev,
        {
          _id: Date.now(),
          vendor: { name: vendorId },
          component,
          amountPaid: Number(vendorAmount),
        },
      ]);

      setVendorAmount("");
    } catch (err) {
      toast.error(err || "Error");
    }
  };

  if (!booking) return <div>Loading...</div>;


  const handleGenerateInvoice = async () => {
    try {
      if (!invoiceAmount) {
        return toast.error("Please enter invoice amount");
      }
      setIsGeneratingInvoice(true);
      const res = await dispatch(
        generateinvoice({
          bookingId: booking._id,
          invoiceAmount: Number(invoiceAmount),
          invoiceType,
          dueDate,
          notes: invoiceNotes,
        })
      ).unwrap();

      toast.success("Invoice generated successfully");

      const invoiceId = res?.data?._id;

      setShowInvoiceModal(false);

      setInvoiceAmount("");
      setInvoiceType("advance");
      setDueDate("");
      setInvoiceNotes("");

      if (invoiceId) {
        navigate(`/admin/invoice/${invoiceId}/preview`);
      }
    } catch (err) {
      toast.error(err || "Failed to generate invoice");
    }
    finally {
      setIsGeneratingInvoice(false);
    }
  };

  const handleInvoiceWhatsApp = (invoice) => {
    const customerName =
      booking?.lead?.name || "Customer";

    const phone = (
      booking?.lead?.phone || ""
    ).replace(/\D/g, "");

    if (!phone) {
      toast.error("Customer phone number not available");
      return;
    }

    // Replace this with your actual short/public invoice URL
    const invoiceUrl =
      invoice?.pdfUrl || "";

    const message = `Hi ${customerName},

Your invoice (${invoice.invoiceNumber}) is ready.

Invoice Amount: ₹${Number(
      invoice.totalAmount || 0
    ).toLocaleString("en-IN")}

You can view/download it here:
${invoiceUrl}

Thank you.`;

    const whatsappUrl = `https://api.whatsapp.com/send?phone=${phone}&text=${encodeURIComponent(
      message
    )}`;

    window.open(whatsappUrl, "_blank");
  };


  const handleSendInvoice = async (invoiceId) => {
    try {
      await dispatch(
        sendInvoice({
          id: invoiceId,
        })
      ).unwrap();

      toast.success("Invoice sent successfully");
    } catch (error) {
      console.error(error);

      toast.error(
        error?.message ||
        error ||
        "Failed to send invoice"
      );
    }
  };
  return (
    <>
      <div className="breadcrumb-bar mb-2">
        <div className="breadcrumb-left">
          <Link to="/hote/home" className="breadcrumb-link active">
            Home
          </Link>
          <span className="breadcrumb-separator"> | </span>
          <Link to="/admin/booking" className="breadcrumb-link active">
            Bookings
          </Link>
          <span className="breadcrumb-separator"> | </span>
          <span className="breadcrumb-link inactive">Booking Details</span>
        </div>
      </div>

      {loading ? (
        <Container className="my-2">

          <Row>

            <Col lg={4} md={12}>
              <Card className="p-4 mb-3 rounded-4">

                <div className="d-flex flex-column align-items-center">
                  <div className="skeleton skeleton-circle mb-3"></div>

                  <div className="skeleton skeleton-text mb-2" style={{ width: "60%" }}></div>
                  <div className="skeleton skeleton-text mb-2" style={{ width: "80%" }}></div>

                  <div className="skeleton skeleton-badge mb-3" style={{ width: "100px" }}></div>
                </div>

                <hr />

                {[...Array(4)].map((_, i) => (
                  <div key={i} className="d-flex justify-content-between mb-2">
                    <div className="skeleton skeleton-text" style={{ width: "40%" }}></div>
                    <div className="skeleton skeleton-text" style={{ width: "40%" }}></div>
                  </div>
                ))}
              </Card>


              <Card className="p-4 rounded-4">
                <div className="skeleton skeleton-text mb-3" style={{ width: "40%" }}></div>

                {[...Array(3)].map((_, i) => (
                  <div key={i} className="d-flex align-items-center mb-3">
                    <div className="skeleton skeleton-circle me-2" style={{ width: "20px", height: "20px" }}></div>
                    <div className="skeleton skeleton-text" style={{ width: "70%" }}></div>
                  </div>
                ))}
              </Card>
            </Col>


            <Col lg={8} md={12}>


              <div className="mb-3 p-2 rounded-4" style={{ background: "#f3f4f6" }}>
                <div className="d-flex gap-2">
                  {[...Array(3)].map((_, i) => (
                    <div
                      key={i}
                      className="skeleton skeleton-badge"
                      style={{ flex: 1, height: "32px" }}
                    ></div>
                  ))}
                </div>
              </div>



              <Card className="p-4 rounded-4 mb-3">
                <div className="skeleton skeleton-text mb-3" style={{ width: "40%" }}></div>

                {[...Array(6)].map((_, i) => (
                  <div key={i} className="skeleton skeleton-text mb-2"></div>
                ))}
              </Card>


              <Card className="p-4 rounded-4">
                <div className="skeleton skeleton-text mb-3" style={{ width: "30%" }}></div>

                {[...Array(4)].map((_, i) => (
                  <div
                    key={i}
                    className="d-flex justify-content-between p-3 mb-2 rounded-3"
                  >
                    <div className="skeleton skeleton-text w-50"></div>
                    <div className="skeleton skeleton-text" style={{ width: "80px" }}></div>
                  </div>
                ))}
              </Card>

            </Col>

          </Row>

        </Container>

      ) : (

        <Container fluid className="lead-container">
          <Row>
            <Col lg={4} md={12}>
              <Card className="profile-card" style={{ border: "none" }}>
                <div className="avatar">{booking?.lead?.name?.charAt(0)}</div>

                <h4 className="name">{booking?.lead?.name}</h4>

                <p className="designation">{booking?.lead?.email || "email"}</p>

                <div className="tags">
                  <span>{booking?.bookingStatus}</span>
                </div>

                <hr />

                <div className="details">
                  <div>
                    <span>Source</span>
                    <b>{booking?.lead?.source}</b>
                  </div>

                  <div>
                    <span>Created</span>
                    <b>
                      {booking?.createdAt
                        ? moment(booking.createdAt).format("DD-MM-YYYY")
                        : ""}
                    </b>
                  </div>

                  <div>
                    <span>Travel Date</span>
                    <b>
                      {booking?.lead?.tentativeTravelDate
                        ? moment(booking.lead?.tentativeTravelDate).format("DD-MM-YYYY")
                        : ""}

                    </b>
                  </div>

                  <div>
                    <span>Pax</span>
                    <b>
                      {booking?.lead?.adults} Adult
                      {booking?.lead?.adults > 1 ? "s" : ""}{" "}
                      {booking?.lead?.children > 0 &&
                        `${booking.lead?.children} ${booking.lead?.children === 1 ? "Child" : "Children"}`}
                    </b>
                  </div>
                </div>
              </Card>
              <Card className="contact-card" style={{ border: "none" }}>
                <p className="label">CONTACT</p>

                <div className="contact-item">
                  <FaEnvelope />
                  {booking?.lead?.email}
                </div>

                <div className="contact-item">
                  <FaPhoneAlt />
                  {booking?.lead?.phone}
                </div>

                <div className="contact-item">
                  <FaMapMarkerAlt />
                  {booking?.lead?.country}
                </div>
              </Card>
            </Col>

            <Col lg={8} md={12}>
              <div className="top-tabs">
                <Row className="mb-3">
                  <Col>
                    <div
                      className="my-2"
                      style={{
                        display: "flex",
                        background: "#f3f4f6",
                        padding: "6px",
                        borderRadius: "16px",
                        width: "100%",
                        maxWidth: "620px",
                      }}
                    >
                      <button
                        onClick={() => setTab("overview")}
                        style={{
                          flex: 1,
                          padding: "8px 12px",
                          border: "none",
                          borderRadius: "12px",
                          background:
                            tab === "overview" ? "#1E0959" : "transparent",
                          color: tab === "overview" ? "#fff" : "#374151",
                          fontWeight: 500,
                          fontSize: "14px",
                          transition: "0.2s",
                        }}
                      >
                        Overview
                      </button>
                      <button
                        onClick={() => setTab("quotation")}
                        style={{
                          flex: 1,
                          padding: "8px 12px",
                          border: "none",
                          borderRadius: "12px",
                          background:
                            tab === "quotation" ? "#1E0959" : "transparent",
                          color:
                            tab === "quotation" ? "#fff" : "#374151",
                          fontWeight: 500,
                          fontSize: "14px",
                          transition: "0.2s",
                        }}
                      >
                        Itinerary
                      </button>
                      <button
                        onClick={() => setTab("invoices")}
                        style={{
                          flex: 1,
                          padding: "8px 12px",
                          border: "none",
                          borderRadius: "12px",
                          background: tab === "invoices" ? "#1E0959" : "transparent",
                          color: tab === "invoices" ? "#fff" : "#374151",
                          fontWeight: 500,
                          fontSize: "14px",
                        }}
                      >
                        Invoices
                      </button>
                      <button
                        onClick={() => setTab("customerpayments")}
                        style={{
                          flex: 1,
                          padding: "8px 12px",
                          border: "none",
                          borderRadius: "12px",
                          background:
                            tab === "customerpayments"
                              ? "#1E0959"
                              : "transparent",
                          color: tab === "customerpayments" ? "#fff" : "#374151",
                          fontWeight: 500,
                          fontSize: "14px",
                          transition: "0.2s",
                        }}
                      >
                        Customer Payment
                      </button>


                      <button
                        onClick={() => setTab("vendorpayment")}
                        style={{
                          flex: 1,
                          padding: "8px 12px",
                          border: "none",
                          borderRadius: "12px",
                          background:
                            tab === "vendorpayment" ? "#1E0959" : "transparent",
                          color: tab === "vendorpayment" ? "#fff" : "#374151",
                          fontWeight: 500,
                          fontSize: "14px",
                          transition: "0.2s",
                        }}
                      >
                        Vendor Payment
                      </button>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* overview ------------- >  */}

              {tab === "overview" && (
                <Container>
                  <Card className="p-3" style={{ border: "none" }}>
                    {loading ? (
                      <>
                        <div className="skeleton skeleton-text w-25 mb-2"></div>

                        <div className="skeleton skeleton-text w-50 mb-1"></div>
                        <div className="skeleton skeleton-text w-50 mb-1"></div>
                        <div className="skeleton skeleton-text w-50 mb-1"></div>
                        <div className="skeleton skeleton-text w-40 mb-1"></div>

                        <div className="skeleton skeleton-text w-60 mb-1"></div>
                        <div className="skeleton skeleton-text w-75 mb-1"></div>
                        <div className="skeleton skeleton-text w-50"></div>
                      </>
                    ) : (
                      <>
                        <p className="label">Booking Summary</p>
                        <p>Total: ₹{booking?.totalAmount || "-"}</p>
                        <p>Paid: ₹{booking?.amountPaid || "-"}</p>
                        <p>Due: ₹{booking?.amountDue || "-"}</p>
                        <p>Status: {booking?.bookingStatus || "-"}</p>

                        <p>Booking Id: {booking?.bookingNumber || "-"}</p>
                        <p>
                          Travel Date:{" "}
                          {booking?.travelDate
                            ? moment(booking.travelDate).format("DD-MM-YYYY")
                            : "-"}
                        </p>
                        <p>Package: {booking?.package?.title || "-"}</p>
                        <p>Package Option: {booking?.selectedOption?.name || "-"}</p>

                      </>
                    )}
                  </Card>
                </Container>
              )}
              {tab === "quotation" && (
                <Container>
                  <Card
                    className="p-3"
                    style={{
                      border: "none",
                      borderRadius: "12px",
                    }}
                  >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <div>
                        <h5 className="mb-1">Finalized Quotation</h5>

                        <small className="text-muted">
                          Quotation No:{" "}
                          {booking?.quotation?.quotationNumber || "-"}
                        </small>
                      </div>

                      {booking?.quotation?.pdfUrl && (
                        <button
                          className="submit-btn"
                          onClick={() =>
                            window.open(
                              booking.quotation.pdfUrl,
                              "_blank"
                            )
                          }
                        >
                          Open PDF
                        </button>
                      )}
                    </div>

                    {booking?.quotation?.pdfUrl ? (
                      <div
                        style={{
                          width: "100%",
                          height: "800px",
                          border: "1px solid #e5e7eb",
                          borderRadius: "10px",
                          overflow: "hidden",
                          background: "#f8f9fa",
                        }}
                      >
                        <iframe
                          src={booking.quotation.pdfUrl}
                          title="Finalized Quotation"
                          width="100%"
                          height="100%"
                          style={{
                            border: "none",
                          }}
                        />
                      </div>
                    ) : (
                      <div
                        className="d-flex justify-content-center align-items-center"
                        style={{
                          height: "300px",
                          color: "#6b7280",
                        }}
                      >
                        <div className="text-center">
                          <h6>No quotation PDF available</h6>

                          <p className="mb-0">
                            The finalized quotation does not have a PDF.
                          </p>
                        </div>
                      </div>
                    )}
                  </Card>
                </Container>
              )}
              {tab === "invoices" && (
                <Container>
                  <Card className="p-3" style={{ border: "none" }}>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5>Invoices</h5>

                      <button
                        className="submit-btn"
                        onClick={() => setShowInvoiceModal(true)}
                      >
                        + Generate Invoice
                      </button>
                    </div>

                    {invoices.length === 0 ? (
                      <div>No invoices found.</div>
                    ) : (
                      invoices.map((invoice) => {
                        const date = invoice.createdAt
                          ? moment(invoice.createdAt).format("DD-MM-YYYY")
                          : "-";

                        const amount = invoice.totalAmount || 0;

                        return (
                          <div className="qt-card" key={invoice._id}>
                            {/* LEFT */}
                            <div className="qt-left">
                              <div className="qt-top">
                                <div className="qt-id">
                                  {invoice.invoiceNumber}
                                </div>

                                <div className="qt-status-badge">
                                  {invoice.invoiceType}
                                </div>
                              </div>

                              <div className="qt-title">
                                {invoice.packageSnapshot?.packageName || "-"}
                              </div>

                              <div className="qt-meta">
                                <span>📅 {date}</span>

                                <span>
                                  🧍 {invoice.packageSnapshot?.totalPax || 0} Pax
                                </span>

                                <span>
                                  💳 {invoice.paymentStatus}
                                </span>
                              </div>
                            </div>

                            {/* RIGHT */}
                            <div className="qt-right">
                              <div className="qt-price">
                                ₹{amount.toLocaleString()}
                              </div>

                              <div className="qt-actions">
                                {/* View PDF */}
                                <button
                                  className="qt-btn view"
                                  onClick={() =>
                                    window.open(invoice.pdfUrl, "_blank")
                                  }
                                >
                                  <FaEye />
                                </button>

                                {/* WhatsApp */}
                                <button
                                  className="qt-btn"
                                  style={{
                                    background: "#dcfce7",
                                    color: "#16a34a",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                  onClick={() =>
                                    handleInvoiceWhatsApp(invoice)
                                  }
                                >
                                  <FaWhatsapp />
                                  WhatsApp
                                </button>

                                {/* Send Email */}
                                <button
                                  className="qt-btn resend"
                                  onClick={() =>
                                    handleSendInvoice(invoice._id)
                                  }
                                >
                                  <FaEnvelope />
                                </button>
                                {invoice.paymentStatus !== "paid" && (
                                  <button
                                    className="qt-btn"
                                    style={{
                                      background: "#dcfce7",
                                      color: "#16a34a",
                                    }}
                                    onClick={() => handleMarkInvoicePaid(invoice._id)}
                                  >
                                    Mark Paid
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </Card>
                </Container>
              )}

              {tab === "customerpayments" && (
                <>
                  <Container>
                    <Card className="p-3 mb-3" style={{ border: "none" }}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 style={{ fontWeight: 600 }}>Customer Payments</h6>


                      </div>
                      <div className="custom-table mt-3">
                        <div className="table-header">
                          <div>Amount</div>
                          <div>Date</div>
                        </div>

                        {loading ? (
                          [...Array(5)].map((_, i) => (
                            <div className="table-row" key={i}>
                              <div className="skeleton skeleton-text w-25"></div>
                              <div className="skeleton skeleton-text w-50"></div>
                            </div>
                          ))
                        ) : customerPayments.length === 0 ? (
                          <div className="empty-state">No payments yet</div>
                        ) : (
                          customerPayments.map((p) => (
                            <div className="table-row" key={p._id}>
                              <div className="amount">
                                ₹{p.amount?.toLocaleString() || "-"}
                              </div>

                              <div className="date">
                                {moment(p.createdAt).format("DD-MM-YYYY")}
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </Card>
                  </Container>
                </>
              )}

              {tab === "vendorpayment" && (
                <>
                  <Container>
                    <Card className="p-3 mb-3" style={{ border: "none" }}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 style={{ fontWeight: 600 }}>Vendor Payments</h6>
                        <button
                          onClick={() => setShowVendorModal(true)}
                          className="submit-btn"
                        >
                          + Add
                        </button>
                      </div>

                      <div className="custom-table mt-3">
                        <div className="table-header grid-4">
                          <div>Date</div>
                          <div>Vendor</div>
                          <div>Component</div>
                          <div>Amount</div>
                        </div>

                        {vendorPayments.length === 0 && (
                          <div className="empty-state">
                            No vendor payments yet
                          </div>
                        )}

                        {loading

                          ? [...Array(5)].map((_, i) => (
                            <div className="table-row grid-4" key={i}>
                              <div className="skeleton skeleton-text w-75"></div>
                              <div className="skeleton skeleton-text w-50"></div>
                              <div className="skeleton skeleton-text w-50"></div>
                              <div className="skeleton skeleton-text w-25"></div>
                            </div>
                          ))

                          : vendorPayments.map((p) => (
                            <div className="table-row grid-4" key={p._id}>
                              <div>
                                {moment(p.createdAt).format("DD-MM-YYYY")}
                              </div>

                              <div>{p.vendor?.name || "-"}</div>

                              <div>{p.component || "-"}</div>

                              <div className="amount">
                                ₹{p.amountPaid?.toLocaleString() || "-"}
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    </Card>
                  </Container>
                </>
              )}

            </Col>
          </Row>
        </Container>
      )}




      <Modal
        show={showVendorModal}
        onHide={() => setShowVendorModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Add Vendor Payment</Modal.Title>
        </Modal.Header>

        <Modal.Body className="d-flex flex-column gap-2">
          <Select
            options={vendorOptions}
            placeholder="Search & select vendor..."
            value={vendorOptions.find((v) => v.value === vendorId) || null}
            onChange={(selected) => {
              setVendorId(selected?.value || "");
            }}
            onInputChange={(inputValue, actionMeta) => {
              if (actionMeta.action === "input-change") {
                fetchVendors(inputValue);
              }
            }}
            isSearchable
            className="basic-single"
            classNamePrefix="select"
          />
          <Form.Select
            value={component}
            onChange={(e) => setComponent(e.target.value)}
          >
            <option value="HOTEL">Hotel</option>
            <option value="VEHICLE">Vehicle</option>
            <option value="FERRY">Ferry</option>
            <option value="ACTIVITY">Activity</option>
          </Form.Select>

          <Form.Control
            placeholder="Amount"
            value={vendorAmount}
            onChange={(e) => setVendorAmount(e.target.value)}
          />
        </Modal.Body>

        <Modal.Footer>
          <button
            className="cancel-btn"
            onClick={() => setShowVendorModal(false)}
          >
            Cancel
          </button>
          <button
            className="submit-btn"
            onClick={async () => {
              await handleVendorPayment();
              setShowVendorModal(false);
            }}
          >
            Save
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showInvoiceModal}
        onHide={() => setShowInvoiceModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Generate Invoice</Modal.Title>
        </Modal.Header>

        <Modal.Body className="d-flex flex-column gap-3">

          <Form.Group>
            <Form.Label>Invoice Amount</Form.Label>

            <Form.Control
              type="text"
              placeholder="Enter invoice amount"
              value={invoiceAmount}
              maxLength={6}
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, "");

                if (value.length <= 6) {
                  setInvoiceAmount(value);
                }
              }}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Invoice Type</Form.Label>
            <Form.Select
              value={invoiceType}
              onChange={(e) => setInvoiceType(e.target.value)}
            >
              <option value="advance">Advance</option>
              <option value="partial">Partial</option>
              <option value="final">Final</option>
            </Form.Select>
          </Form.Group>

          <Form.Group>
            <Form.Label>Due Date</Form.Label>
            <Form.Control
              type="date"
              value={dueDate}
              //  min={new Date().toISOString().split("T")[0]}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={invoiceNotes}
              onChange={(e) => setInvoiceNotes(e.target.value)}
            />
          </Form.Group>

        </Modal.Body>

        <Modal.Footer>
          <button
            className="cancel-btn"
            onClick={() => setShowInvoiceModal(false)}
          >
            Cancel
          </button>

          <button
            className="submit-btn"
            onClick={handleGenerateInvoice}
            disabled={isGeneratingInvoice}
            style={{
              opacity: isGeneratingInvoice ? 0.7 : 1,
              cursor: isGeneratingInvoice ? "not-allowed" : "pointer",
            }}
          >
            {isGeneratingInvoice ? "Generating..." : "Generate"}
          </button>
        </Modal.Footer>
      </Modal>


    </>
  );
}
